import type {
  AccessSetupResponse,
  AccessStatusResponse,
  AppSettingsResponse,
  BillingCheckoutsResponse,
  SubscriptionStatusResponse,
  CreateStripeCheckoutRequest,
  CreateStripeCheckoutResponse,
  StripePortalResponse,
  DatabaseBackupCatalogResponse,
  CreateDatabaseBackupResponse,
  AccessLoginRequest,
  AccessLoginResponse,
  AdminQrCard,
  AdminQrCardsResponse,
  AccessUserListResponse,
  AccessUserResponse,
  AdminOverviewResponse,
  ClearLoginJournalResponse,
  CreateOrderRequest,
  CreateAccessUserRequest,
  CreateOrderResponse,
  CreateVenuePhotoRequest,
  CreateMenuItemRequest,
  DeleteAccessUserRequest,
  InitialAccessSetupRequest,
  MenuItemResponse,
  MenuListResponse,
  LoginJournalResponse,
  LocalNetworkInfoResponse,
  OrderListResponse,
  CreateTableRequest,
  DeleteTableRequest,
  TableAccessResponse,
  WaiterCallListResponse,
  WaiterCallResponse,
  TableResponse,
  TableListResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  UpdateOrderTimerRequest,
  UpdateOrderTimerResponse,
  UpdateAccessSetupRequest,
  UpdateAppSettingsRequest,
  UpdateAccessUserRequest,
  UpdateMenuItemRequest,
  UpdateTableRequest,
  RecoverPasswordRequest,
  RecoverPasswordResponse,
  RestoreDatabaseBackupRequest,
  RestoreDatabaseBackupResponse,
  UpdateDatabaseBackupSettingsRequest,
  UpdateDatabaseBackupSettingsResponse,
  SalesStatisticsResponse,
  SupportRequestPayload,
  SupportRequestResponse,
  CreatePortalReviewRequest,
  CreatePortalReviewResponse,
  CreatePortalReportRequest,
  CreatePortalReportResponse,
  PortalReview,
  PortalAdminAuthRequest,
  PortalAdminAuthResponse,
  PortalAdminBillingResponse,
  PortalReportsResponse,
  PortalVenueListResponse,
  PortalVenueProfileResponse,
  UpdatePortalReportRequest,
  UpdatePortalReportResponse,
  UpdatePortalVenueEnabledRequest,
  UpdatePortalVenueEnabledResponse,
  UpdatePortalVenueLocationRequest,
  UpdatePortalVenueLocationResponse,
  VenuePhotoListResponse,
  VenuePhotoResponse,
  VerifyTableAccessRequest,
} from '../shared/api-contract';
import { ApiEndpoints } from '../shared/api-contract';
import type {
  CostingAnalytics,
  CostingAnalyticsParams,
  CostingMode,
  CreateIngredientRequest,
  CreateSupplierRequest,
  DeleteIngredientResponse,
  DeleteSupplierResponse,
  DishCostHistoryResponse,
  DishCostingResponse,
  IngredientListResponse,
  IngredientPriceHistoryResponse,
  IngredientResponse,
  ManualDishCost,
  RecipeResponse,
  SetCostingModeRequest,
  SupplierListResponse,
  SupplierResponse,
  UpdateIngredientRequest,
  UpdateRecipeRequest,
  UpdateSupplierRequest,
} from '../shared/types/costing';
import { API_BASE_URL, resolveApiUrl, resolvePortalUrl } from '../config/runtime';

export type { AdminQrCard };

const DESKTOP_SESSION_STORAGE_KEY = 'cafe-desktop-session';

export function getDesktopSessionCredentials() {
  if (typeof window === 'undefined') {
    return { userId: '', loginId: '' };
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(DESKTOP_SESSION_STORAGE_KEY) || 'null') as
      { user?: { id?: string }; loginEntry?: { id?: string } } | null;
    return {
      userId: parsed?.user?.id || '',
      // loginEntry is already stored alongside user at login time (see store/session.ts);
      // its id proves this is a real login, not just knowledge of a (non-secret) user id.
      loginId: parsed?.loginEntry?.id || '',
    };
  } catch {
    return { userId: '', loginId: '' };
  }
}

// The saved sign-in can stop being valid (login journal cleared, signed out
// elsewhere). Then every admin action answers 401: drop it and show the
// sign-in screen instead of failing silently on every button.
function dropStaleSession(status: number, payload: unknown) {
  if (typeof window === 'undefined' || status !== 401) return;
  const { userId } = getDesktopSessionCredentials();
  const text = payload && typeof payload === 'object' ? String((payload as { error?: string }).error || '') : '';
  if (!userId || !/Admin access required/i.test(text)) return;
  try { window.localStorage.removeItem(DESKTOP_SESSION_STORAGE_KEY); } catch { /* ignore */ }
  if (!/^\/v2\/start/.test(window.location.pathname)) window.location.assign('/v2/start');
}

function buildJsonHeaders(body?: unknown) {
  const headers: Record<string, string> = body ? { 'Content-Type': 'application/json' } : {};
  const { userId, loginId } = getDesktopSessionCredentials();

  if (userId) {
    headers['X-Cafe-User-Id'] = userId;
  }

  if (loginId) {
    headers['X-Cafe-Login-Id'] = loginId;
  }

  return Object.keys(headers).length > 0 ? headers : undefined;
}

// Ошибка запроса с сохранением HTTP-статуса и тела ответа сервера
// (нужно, например, чтобы поймать 402 «нужна подписка» и показать баннер).
export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(endpoint: { path: string; method: string }, body?: unknown): Promise<T> {
  const response = await fetch(resolveApiUrl(endpoint.path), {
    method: endpoint.method,
    headers: buildJsonHeaders(body),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    dropStaleSession(response.status, payload);
    const message = (payload && typeof payload === 'object' && 'message' in payload && (payload as { message?: string }).message)
      || (payload && typeof payload === 'object' && 'error' in payload && (payload as { error?: string }).error)
      || response.statusText;
    throw new ApiError(String(message), response.status, payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
}

async function portalRequest<T>(endpoint: { path: string; method: string }, body?: unknown): Promise<T> {
  const response = await fetch(resolvePortalUrl(endpoint.path), {
    method: endpoint.method,
    headers: buildJsonHeaders(body),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.error || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
}

export const getMenu = (): Promise<MenuListResponse> => request<MenuListResponse>(ApiEndpoints.menu.list);
export const createMenuItem = (payload: CreateMenuItemRequest): Promise<MenuItemResponse> =>
  request<MenuItemResponse>(ApiEndpoints.menu.createItem, payload);
export const updateMenuItem = (itemId: string, payload: UpdateMenuItemRequest): Promise<MenuItemResponse> =>
  request<MenuItemResponse>(ApiEndpoints.menu.updateItem(itemId), payload);
export const deleteMenuItem = (itemId: string): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.menu.deleteItem(itemId));
// Staff get all of today's orders; a guest passes its table and table token.
export const getOrders = async (tableId?: string): Promise<OrderListResponse> => {
  if (!tableId) {
    return request<OrderListResponse>(ApiEndpoints.orders.list);
  }
  const headers: Record<string, string> = { ...(buildJsonHeaders() || {}), 'X-Cafe-Table-Token': readTableOrderToken(tableId) };
  const response = await fetch(resolveApiUrl(`${ApiEndpoints.orders.list.path}?tableId=${encodeURIComponent(tableId)}`), { method: 'GET', headers });
  if (!response.ok) {
    throw new ApiError(response.statusText, response.status, null);
  }
  return response.json() as Promise<OrderListResponse>;
};
const TABLE_TOKEN_PREFIX = 'cafe-table-token:';
export function saveTableOrderToken(tableId: string, token: string) {
  try {
    window.localStorage.setItem(`${TABLE_TOKEN_PREFIX}${tableId}`, token);
  } catch {
    /* private mode: the table code is asked again */
  }
}
function readTableOrderToken(tableId: string): string {
  try {
    return window.localStorage.getItem(`${TABLE_TOKEN_PREFIX}${tableId}`) || '';
  } catch {
    return '';
  }
}
// Staff are recognised by their session; a guest order carries the table token.
export const createOrder = async (payload: CreateOrderRequest): Promise<CreateOrderResponse> => {
  const token = payload.tableId ? readTableOrderToken(payload.tableId) : '';
  if (!token) {
    return request<CreateOrderResponse>(ApiEndpoints.orders.create, payload);
  }
  const headers: Record<string, string> = { ...(buildJsonHeaders(payload) || {}), 'X-Cafe-Table-Token': token };
  const response = await fetch(resolveApiUrl(ApiEndpoints.orders.create.path), { method: ApiEndpoints.orders.create.method, headers, body: JSON.stringify(payload) });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message = (body && typeof body === 'object' && 'error' in body && (body as { error?: string }).error) || response.statusText;
    throw new ApiError(String(message), response.status, body);
  }
  return response.json() as Promise<CreateOrderResponse>;
};
export const updateOrderStatus = (
  orderId: string,
  payload: UpdateOrderStatusRequest,
): Promise<UpdateOrderStatusResponse> =>
  request<UpdateOrderStatusResponse>(ApiEndpoints.orders.updateStatus(orderId), payload);
export const updateOrderTimer = (
  orderId: string,
  payload: UpdateOrderTimerRequest,
): Promise<UpdateOrderTimerResponse> =>
  request<UpdateOrderTimerResponse>(ApiEndpoints.orders.updateTimer(orderId), payload);
export const getAdminOverview = (): Promise<AdminOverviewResponse> => request<AdminOverviewResponse>(ApiEndpoints.admin.overview);
export const getAdminSalesStatistics = (date: string): Promise<SalesStatisticsResponse> =>
  request<SalesStatisticsResponse>({ method: 'GET', path: `${ApiEndpoints.admin.statistics.path}?date=${encodeURIComponent(date)}` });
export const getLoginJournal = (): Promise<LoginJournalResponse> => request<LoginJournalResponse>(ApiEndpoints.admin.loginJournal);
export const clearLoginJournal = (): Promise<ClearLoginJournalResponse> => request<ClearLoginJournalResponse>(ApiEndpoints.admin.clearLoginJournal);
export const getLocalNetworkInfo = (): Promise<LocalNetworkInfoResponse> => request<LocalNetworkInfoResponse>(ApiEndpoints.admin.localNetwork);
export const getDatabaseBackups = (): Promise<DatabaseBackupCatalogResponse> => request<DatabaseBackupCatalogResponse>(ApiEndpoints.admin.backups);
export const createDatabaseBackup = (): Promise<CreateDatabaseBackupResponse> => request<CreateDatabaseBackupResponse>(ApiEndpoints.admin.createBackup, {});
export const restoreDatabaseBackup = (payload: RestoreDatabaseBackupRequest): Promise<RestoreDatabaseBackupResponse> =>
  request<RestoreDatabaseBackupResponse>(ApiEndpoints.admin.restoreBackup, payload);
export const updateDatabaseBackupSettings = (payload: UpdateDatabaseBackupSettingsRequest): Promise<UpdateDatabaseBackupSettingsResponse> =>
  request<UpdateDatabaseBackupSettingsResponse>(ApiEndpoints.admin.updateBackupSettings, payload);
export const submitSupportRequest = (payload: SupportRequestPayload): Promise<SupportRequestResponse> =>
  request<SupportRequestResponse>(ApiEndpoints.support.submit, payload);
export const getSubscriptionStatus = (): Promise<SubscriptionStatusResponse> =>
  request<SubscriptionStatusResponse>(ApiEndpoints.billing.subscription);
export const getBillingCheckouts = (): Promise<BillingCheckoutsResponse> =>
  request<BillingCheckoutsResponse>(ApiEndpoints.billing.checkouts);
export const createStripeCheckout = (
  payload: CreateStripeCheckoutRequest,
): Promise<CreateStripeCheckoutResponse> =>
  request<CreateStripeCheckoutResponse>(ApiEndpoints.billing.createStripeCheckout, payload);
export const openStripePortal = (lang?: string): Promise<StripePortalResponse> =>
  request<StripePortalResponse>(ApiEndpoints.billing.stripePortal, lang ? { lang } : {});
export const getPortalVenueProfile = (): Promise<PortalVenueProfileResponse> =>
  request<PortalVenueProfileResponse>(ApiEndpoints.portal.venueProfile);
export const getPortalVenues = (): Promise<PortalVenueListResponse> =>
  request<PortalVenueListResponse>(ApiEndpoints.portal.venues);
export const getPublicPortalVenues = (): Promise<PortalVenueListResponse> =>
  portalRequest<PortalVenueListResponse>(ApiEndpoints.portal.venues);

const isDesktopRuntime = () => typeof window !== 'undefined' && Boolean(window.__CAFE_DESKTOP_RUNTIME__);

const requestLocalDesktopFallback = async <T>(endpoint: { path: string; method: string }, body?: unknown): Promise<T> => {
  const normalizedPath = endpoint.path.startsWith('/') ? endpoint.path : `/${endpoint.path}`;
  const response = await fetch(`http://127.0.0.1:4000${normalizedPath}`, {
    method: endpoint.method,
    headers: buildJsonHeaders(body),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = (payload && typeof payload === 'object' && 'message' in payload && (payload as { message?: string }).message)
      || (payload && typeof payload === 'object' && 'error' in payload && (payload as { error?: string }).error)
      || response.statusText;
    throw new ApiError(String(message), response.status, payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json();
};

export const updatePortalVenueLocation = async (payload: UpdatePortalVenueLocationRequest): Promise<UpdatePortalVenueLocationResponse> => {
  try {
    return await request<UpdatePortalVenueLocationResponse>(ApiEndpoints.portal.updateVenueLocation, payload);
  } catch (error) {
    const failedToReachApi = error instanceof TypeError && String(error.message || '').includes('fetch');
    const alreadyLocalApi = /^https?:\/\/(localhost|127\.0\.0\.1):4000$/i.test(API_BASE_URL);
    if (!isDesktopRuntime() || !failedToReachApi || alreadyLocalApi) {
      throw error;
    }

    return requestLocalDesktopFallback<UpdatePortalVenueLocationResponse>(ApiEndpoints.portal.updateVenueLocation, payload);
  }
};
export const createPortalReview = (payload: CreatePortalReviewRequest): Promise<CreatePortalReviewResponse> =>
  portalRequest<CreatePortalReviewResponse>(ApiEndpoints.portal.createReview, payload);
export const createPortalReport = (payload: CreatePortalReportRequest): Promise<CreatePortalReportResponse> =>
  portalRequest<CreatePortalReportResponse>(ApiEndpoints.portal.createReport, payload);
export const getVenueReviews = (venueId: string): Promise<{ reviews: PortalReview[] }> =>
  portalRequest<{ reviews: PortalReview[] }>(ApiEndpoints.portal.venueReviews(venueId));
export const authenticatePortalOwner = (payload: PortalAdminAuthRequest): Promise<PortalAdminAuthResponse> =>
  request<PortalAdminAuthResponse>(ApiEndpoints.portal.adminAuth, payload);
export const getPortalAdminVenues = (ownerEmail: string): Promise<PortalVenueListResponse> =>
  request<PortalVenueListResponse>({ method: 'GET', path: `${ApiEndpoints.portal.adminVenues.path}?ownerEmail=${encodeURIComponent(ownerEmail)}` });
export const getPortalAdminBilling = (ownerEmail: string): Promise<PortalAdminBillingResponse> =>
  request<PortalAdminBillingResponse>({ method: 'GET', path: `${ApiEndpoints.portal.adminBilling.path}?ownerEmail=${encodeURIComponent(ownerEmail)}` });
export const getPortalAdminReports = (ownerEmail: string): Promise<PortalReportsResponse> =>
  request<PortalReportsResponse>({ method: 'GET', path: `${ApiEndpoints.portal.adminReports.path}?ownerEmail=${encodeURIComponent(ownerEmail)}` });
export const updatePortalAdminVenue = (
  venueId: string,
  payload: UpdatePortalVenueEnabledRequest & { ownerEmail: string },
): Promise<UpdatePortalVenueEnabledResponse> =>
  request<UpdatePortalVenueEnabledResponse>(ApiEndpoints.portal.updateAdminVenue(venueId), payload);
export const updatePortalAdminReport = (
  reportId: string,
  payload: UpdatePortalReportRequest & { ownerEmail: string },
): Promise<UpdatePortalReportResponse> =>
  request<UpdatePortalReportResponse>(ApiEndpoints.portal.updateAdminReport(reportId), payload);
export const getVenuePhotos = (): Promise<VenuePhotoListResponse> => request<VenuePhotoListResponse>(ApiEndpoints.venue.photos);
export const createVenuePhoto = (payload: CreateVenuePhotoRequest): Promise<VenuePhotoResponse> =>
  request<VenuePhotoResponse>(ApiEndpoints.venue.createPhoto, payload);
export const deleteVenuePhoto = (photoId: string): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.venue.deletePhoto(photoId));
export const getTables = (): Promise<TableListResponse> => request<TableListResponse>(ApiEndpoints.tables.list);
export const getWaiterCalls = (): Promise<WaiterCallListResponse> =>
  request<WaiterCallListResponse>(ApiEndpoints.tables.waiterCalls);
export const createTable = (payload: CreateTableRequest): Promise<TableResponse> =>
  request<TableResponse>(ApiEndpoints.tables.create, payload);
export const updateTable = (tableId: string, payload: UpdateTableRequest): Promise<TableResponse> =>
  request<TableResponse>(ApiEndpoints.tables.update(tableId), payload);
export const deleteTable = (tableId: string, payload?: DeleteTableRequest): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.tables.delete(tableId), payload);
export const getTableAccess = (accessCode: string): Promise<TableAccessResponse> =>
  request<TableAccessResponse>(ApiEndpoints.tables.access(accessCode));
export const verifyTableAccess = (accessCode: string, payload: VerifyTableAccessRequest): Promise<TableAccessResponse> =>
  request<TableAccessResponse>(ApiEndpoints.tables.verifyAccess(accessCode), payload);
export const callWaiter = (tableId: string): Promise<WaiterCallResponse> =>
  request<WaiterCallResponse>(ApiEndpoints.tables.callWaiter(tableId), {});
export const resolveWaiterCall = (tableId: string): Promise<WaiterCallResponse> =>
  request<WaiterCallResponse>(ApiEndpoints.tables.resolveWaiterCall(tableId), {});
export const loginWithUsername = (payload: AccessLoginRequest): Promise<AccessLoginResponse> =>
  request<AccessLoginResponse>(ApiEndpoints.access.login, payload);
export const loginWithQrToken = (payload: AccessLoginRequest): Promise<AccessLoginResponse> =>
  request<AccessLoginResponse>(ApiEndpoints.access.loginWithQr, payload);
export const getAccessUsers = (): Promise<AccessUserListResponse> =>
  request<AccessUserListResponse>(ApiEndpoints.access.users);
export const createAccessUser = (payload: CreateAccessUserRequest): Promise<AccessUserResponse> =>
  request<AccessUserResponse>(ApiEndpoints.access.createUser, payload);
export const updateAccessUser = (userId: string, payload: UpdateAccessUserRequest): Promise<AccessUserResponse> =>
  request<AccessUserResponse>(ApiEndpoints.access.updateUser(userId), payload);
export const deleteAccessUser = (userId: string, payload: DeleteAccessUserRequest): Promise<{ success: boolean; id: string }> =>
  request<{ success: boolean; id: string }>(ApiEndpoints.access.deleteUser(userId), payload);
export const getAccessSetup = (): Promise<AccessSetupResponse> =>
  request<AccessSetupResponse>(ApiEndpoints.access.getSetup);
export const getAccessStatus = (): Promise<AccessStatusResponse> =>
  request<AccessStatusResponse>(ApiEndpoints.access.status);
export const getAdminQrCards = (): Promise<AdminQrCardsResponse> =>
  request<AdminQrCardsResponse>(ApiEndpoints.access.adminQrCards);
export const updateAccessSetup = (payload: UpdateAccessSetupRequest): Promise<AccessSetupResponse> =>
  request<AccessSetupResponse>(ApiEndpoints.access.updateSetup, payload);
export const getAppSettings = (): Promise<AppSettingsResponse> =>
  request<AppSettingsResponse>(ApiEndpoints.appSettings.get);
export const updateAppSettings = (payload: UpdateAppSettingsRequest): Promise<AppSettingsResponse> =>
  request<AppSettingsResponse>(ApiEndpoints.appSettings.update, payload);
export const createInitialAccessSetup = (payload: InitialAccessSetupRequest): Promise<AccessLoginResponse> =>
  request<AccessLoginResponse>(ApiEndpoints.access.initialSetup, payload);
export const recoverPassword = (payload: RecoverPasswordRequest): Promise<RecoverPasswordResponse> =>
  request<RecoverPasswordResponse>(ApiEndpoints.access.recoverPassword, payload);
export const recordLogout = (): Promise<{ success: boolean }> =>
  request<{ success: boolean }>({ method: 'POST', path: '/api/access/logout' }, { deviceName: getDeviceNameForAudit() });

function getDeviceNameForAudit() {
  if (typeof navigator === 'undefined') {
    return 'Desktop app';
  }

  return `${navigator.platform || 'Desktop'} • ${navigator.userAgent}`;
}

export const uploadImage = async (file: File): Promise<{ imagePath: string }> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(resolveApiUrl('/api/uploads/images'), {
    method: 'POST',
    headers: buildJsonHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    dropStaleSession(response.status, payload);
    throw new Error(payload?.error || response.statusText);
  }

  return response.json();
};

// ── Profit Assistant (costing) ──────────────────────────────────────────────

// Suppliers
export const getSuppliers = (): Promise<SupplierListResponse> =>
  request<SupplierListResponse>(ApiEndpoints.costing.suppliers);
export const createSupplier = (payload: CreateSupplierRequest): Promise<SupplierResponse> =>
  request<SupplierResponse>(ApiEndpoints.costing.createSupplier, payload);
export const updateSupplier = (supplierId: string, payload: UpdateSupplierRequest): Promise<SupplierResponse> =>
  request<SupplierResponse>(ApiEndpoints.costing.updateSupplier(supplierId), payload);
export const deleteSupplier = (supplierId: string): Promise<DeleteSupplierResponse> =>
  request<DeleteSupplierResponse>(ApiEndpoints.costing.deleteSupplier(supplierId));

// Ingredients
export const getIngredients = (): Promise<IngredientListResponse> =>
  request<IngredientListResponse>(ApiEndpoints.costing.ingredients);
export const getIngredient = (ingredientId: string): Promise<IngredientResponse> =>
  request<IngredientResponse>(ApiEndpoints.costing.ingredient(ingredientId));
export const createIngredient = (payload: CreateIngredientRequest): Promise<IngredientResponse> =>
  request<IngredientResponse>(ApiEndpoints.costing.createIngredient, payload);
export const updateIngredient = (ingredientId: string, payload: UpdateIngredientRequest): Promise<IngredientResponse> =>
  request<IngredientResponse>(ApiEndpoints.costing.updateIngredient(ingredientId), payload);
export const deleteIngredient = (ingredientId: string): Promise<DeleteIngredientResponse> =>
  request<DeleteIngredientResponse>(ApiEndpoints.costing.deleteIngredient(ingredientId));
export const getIngredientPriceHistory = (ingredientId: string): Promise<IngredientPriceHistoryResponse> =>
  request<IngredientPriceHistoryResponse>(ApiEndpoints.costing.ingredientPriceHistory(ingredientId));
// A "new price" for an ingredient is a PATCH of its package price/size: the
// server recomputes unitCost, writes price history and recalculates every
// dish that uses the ingredient.
export const addIngredientPrice = (
  ingredientId: string,
  payload: Pick<UpdateIngredientRequest, 'packagePrice' | 'packageSize'>,
): Promise<IngredientResponse> =>
  request<IngredientResponse>(ApiEndpoints.costing.updateIngredient(ingredientId), payload);

// Recipes & dish costing
export const getRecipe = (dishId: string): Promise<RecipeResponse> =>
  request<RecipeResponse>(ApiEndpoints.costing.dishRecipe(dishId));
export const updateRecipe = (dishId: string, payload: UpdateRecipeRequest): Promise<DishCostingResponse> =>
  request<DishCostingResponse>(ApiEndpoints.costing.updateDishRecipe(dishId), payload);
export const getDishCosting = (dishId: string): Promise<DishCostingResponse> =>
  request<DishCostingResponse>(ApiEndpoints.costing.dishCosting(dishId));
export const setManualDishCost = (dishId: string, payload: ManualDishCost): Promise<DishCostingResponse> =>
  request<DishCostingResponse>(ApiEndpoints.costing.setManualDishCost(dishId), payload);
export const setDishCostingMode = (dishId: string, mode: CostingMode): Promise<DishCostingResponse> =>
  request<DishCostingResponse>(ApiEndpoints.costing.setDishCostMode(dishId), { mode } satisfies SetCostingModeRequest);
export const getDishCostHistory = (dishId: string): Promise<DishCostHistoryResponse> =>
  request<DishCostHistoryResponse>(ApiEndpoints.costing.dishCostHistory(dishId));

// Analytics
// v2 screens: dish order by profit (ids only, safe for waiters/guests) and an
// admin-only cost summary for every dish in one call.
export const getMenuRanking = (): Promise<{ order: string[]; recommended: string[] }> =>
  request<{ order: string[]; recommended: string[] }>({ path: '/api/menu/ranking', method: 'GET' });
export interface DishCostSummary {
  menuItemId: string;
  price: number;
  currency: string;
  mode: CostingMode;
  hasRecipe: boolean;
  recipeComplete: boolean;
  reliable: boolean;
  cost: number;
  profit: number;
  marginPct: number | null;
  foodCostPct: number | null;
}
export const getDishesCostSummary = (): Promise<{ dishes: DishCostSummary[] }> =>
  request<{ dishes: DishCostSummary[] }>({ path: '/api/costing/dishes-summary', method: 'GET' });
export const getSalesLastWeek = (): Promise<{ revenue: number; orders: number; weekday: number }> =>
  request<{ revenue: number; orders: number; weekday: number }>({ path: '/api/v2/sales-last-week', method: 'GET' });

export const getCostingAnalytics = (params: CostingAnalyticsParams = {}): Promise<CostingAnalytics> => {
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.thresholds?.popularity != null) query.set('popularityThreshold', String(params.thresholds.popularity));
  if (params.thresholds?.profitability != null) query.set('profitabilityThreshold', String(params.thresholds.profitability));
  return request<CostingAnalytics>(ApiEndpoints.costing.analytics(query.toString()));
};

export const reissueAccessPass = (userId: string): Promise<{ id: string; qrToken: string }> =>
  request<{ id: string; qrToken: string }>({ path: `/api/v2/access/users/${encodeURIComponent(userId)}/reissue-pass`, method: 'POST' }, {});
export const setMenuCurrency = (currency: string): Promise<{ currency: string; changed: number }> =>
  request<{ currency: string; changed: number }>({ path: '/api/v2/menu/currency', method: 'POST' }, { currency });
export interface IngredientUse { ingredientId: string; menuItemId: string; quantity: number; recipeUnit: string; wastePercent: number; dishName: string; price: number; currency: string; costMode: string }
export const getIngredientUsage = (): Promise<{ usage: IngredientUse[] }> =>
  request<{ usage: IngredientUse[] }>({ path: '/api/v2/ingredients/usage', method: 'GET' });
export const getIngredientMeta = (): Promise<{ meta: Array<{ ingredientId: string; wastePercent: number }> }> =>
  request<{ meta: Array<{ ingredientId: string; wastePercent: number }> }>({ path: '/api/v2/ingredients/meta', method: 'GET' });
export const setIngredientWaste = (ingredientId: string, wastePercent: number): Promise<{ ingredientId: string; wastePercent: number }> =>
  request<{ ingredientId: string; wastePercent: number }>({ path: `/api/v2/ingredients/${encodeURIComponent(ingredientId)}/meta`, method: 'PUT' }, { wastePercent });
export interface PrepLine { ingredientId: string; quantity: number; recipeUnit: string; wastePercent: number }
export interface Prep { ingredientId: string; yieldQty: number; yieldUnit: string; lines: PrepLine[] }
export const getPreps = (): Promise<{ preps: Prep[] }> => request<{ preps: Prep[] }>({ path: '/api/v2/preps', method: 'GET' });
export const createPrep = (payload: { name: string; yieldQty: number; yieldUnit: string; currency?: string }): Promise<{ ingredient: import('../shared/types/costing').Ingredient; prep: Prep }> =>
  request({ path: '/api/v2/preps', method: 'POST' }, payload);
export const savePrep = (id: string, payload: { yieldQty: number; yieldUnit: string; lines: PrepLine[] }): Promise<{ ingredient: import('../shared/types/costing').Ingredient; prep: Prep }> =>
  request({ path: `/api/v2/preps/${encodeURIComponent(id)}`, method: 'PUT' }, payload);

// Supplier invoices (закупки)
export interface PurchaseLine { ingredientId: string; ingredientName: string; quantity: number; unit: string; sum: number; unitCost: number; prevUnitCost: number | null }
export interface Purchase { id: string; supplierId: string | null; supplierName: string; date: string; number: string; note: string; total: number; currency: string; createdAt: string; lines: PurchaseLine[] }
export interface PurchaseLineInput { ingredientId?: string; newName?: string; quantity: number; unit: string; sum: number }
export const getPurchases = (): Promise<{ purchases: Purchase[] }> => request<{ purchases: Purchase[] }>({ path: '/api/v2/purchases', method: 'GET' });
export const createPurchase = (payload: { supplierName?: string; supplierId?: string; date: string; number?: string; currency?: string; lines: PurchaseLineInput[] }): Promise<{ purchase: Purchase }> =>
  request<{ purchase: Purchase }>({ path: '/api/v2/purchases', method: 'POST' }, payload);
export interface InvoiceDraftLine { name: string; invoiceName: string; matched: boolean; quantity: number; unit: string; sum: number }
export interface InvoiceDraft { supplier: string; date: string; number: string; currency: string; total: number | null; lines: InvoiceDraftLine[] }
export const readInvoicePhoto = (payload: { image: string; products: string[]; language: string }): Promise<{ available: boolean; draft?: InvoiceDraft; error?: string }> =>
  request<{ available: boolean; draft?: InvoiceDraft; error?: string }>({ path: '/api/v2/purchases/read', method: 'POST' }, payload);

// Sign in on a screen by confirming from a phone (QR on the screen).
export const startQrLogin = (deviceName: string): Promise<{ id: string; secret: string; expiresInMs: number; lanBaseUrl: string }> =>
  request({ path: '/api/v2/qr-login/start', method: 'POST' }, { deviceName });
export const getQrLoginStatus = (id: string, secret: string): Promise<{ status: 'pending' | 'approved' | 'expired'; user?: import('../shared/types').AccessUser; loginEntry?: import('../shared/types').LoginJournalEntry }> =>
  request({ path: `/api/v2/qr-login/${encodeURIComponent(id)}/status?secret=${encodeURIComponent(secret)}`, method: 'GET' });
export const getQrLoginInfo = (id: string): Promise<{ status: string; deviceName: string; signedIn: boolean; fullName: string }> =>
  request({ path: `/api/v2/qr-login/${encodeURIComponent(id)}`, method: 'GET' });
export const approveQrLogin = (id: string): Promise<{ ok: boolean; deviceName: string }> =>
  request({ path: `/api/v2/qr-login/${encodeURIComponent(id)}/approve`, method: 'POST' }, {});

// Venue logo (v2).
// First data entry with AI (paid «ИИ-ассистент»): drafts the owner checks.
export interface MenuDraft { currency: string; sections: { name: string; dishes: { name: string; price: number | null; description: string }[] }[] }
export interface RecipeDraft { note: string; lines: { name: string; matched?: boolean; quantity: number; unit: 'g' | 'kg' | 'ml' | 'l' | 'pcs' }[] }
export const readMenuWithAi = (payload: { images: string[]; sections: string[]; language: string }): Promise<{ available: boolean; draft?: MenuDraft }> =>
  request({ path: '/api/v2/setup/menu-read', method: 'POST' }, payload);
export const draftRecipeWithAi = (payload: { dish: string; description: string; products: string[]; language: string }): Promise<{ available: boolean; draft?: RecipeDraft }> =>
  request({ path: '/api/v2/setup/recipe-draft', method: 'POST' }, payload);
export interface DishCheck { products: { name: string; allergens: string[] }[]; extra: string[]; tags: string[]; description: string; warnings: string[] }
export const checkDishWithAi = (payload: { dish: string; description: string; products: { name: string; allergens: string[] | null }[]; language: string }): Promise<{ available: boolean; draft?: DishCheck }> =>
  request({ path: '/api/v2/setup/dish-check', method: 'POST' }, payload);
// The venue's format, cuisines and limits (all AI helpers follow them).
export interface VenueStyle { format: string; cuisines: string[]; limits: string[] }
export const getVenueStyle = (): Promise<VenueStyle> => request({ path: '/api/v2/venue-style', method: 'GET' });
export const setVenueStyle = (style: VenueStyle): Promise<VenueStyle> => request({ path: '/api/v2/venue-style', method: 'PUT' }, style);
export const guessVenueStyle = (payload: { menu: string[]; language: string }): Promise<{ available: boolean; draft?: { format: string; cuisines: string[] } }> =>
  request({ path: '/api/v2/setup/style-guess', method: 'POST' }, payload);
// Menu sections (v2 «Меню»).
export const createMenuSection = (name: string): Promise<{ id: string; name: string }> => request({ path: '/api/v2/menu/sections', method: 'POST' }, { name });
export const renameMenuSection = (id: string, name: string): Promise<{ id: string; name: string }> => request({ path: `/api/v2/menu/sections/${encodeURIComponent(id)}`, method: 'PATCH' }, { name });
export const deleteMenuSection = (id: string): Promise<{ id: string }> => request({ path: `/api/v2/menu/sections/${encodeURIComponent(id)}`, method: 'DELETE' });
export const getBranding = (): Promise<{ logoPath: string }> => request({ path: '/api/v2/branding', method: 'GET' });
export const setBranding = (logoPath: string): Promise<{ logoPath: string }> => request({ path: '/api/v2/branding', method: 'PUT' }, { logoPath });

// Sales for a period (v2 Продажи).
export interface SalesTotals { revenue: number; profit: number | null; profitCoverage: number; orders: number; avgCheck: number }
export interface SalesSummary {
  period: 'day' | 'week' | 'month'; start: string; currency: string;
  current: SalesTotals; previous: SalesTotals;
  series: { label: string; revenue: number; profit: number }[];
  dishes: { menuItemId: string; name: string; qty: number; revenue: number; profit: number | null }[];
}
export const getSales = (period: 'day' | 'week' | 'month'): Promise<SalesSummary> => request({ path: `/api/v2/sales?period=${period}`, method: 'GET' });

// Dish ideas from the profit assistant (v2 «Новые блюда»).
export interface IdeaIngredient { name: string; have: boolean; quantity: number; unit: string; wastePercent: number; estPrice: number; cost: number }
export interface DishIdea {
  id: string; day: string; status: 'new' | 'added' | 'dismissed'; menuItemId: string;
  name: string; description: string; category: string; tags: string[]; allergens: string[];
  price: number; priceLow: number; priceHigh: number; prepMinutes: number; yieldGrams: number; equipment: string;
  ingredients: IdeaIngredient[]; cost: number; profit: number; foodCostPct: number | null; missing: number; suspect?: { name: string; perUnit: number; unit: string }[];
}
export interface IdeasResponse { day: string; triedToday: boolean; aiActive?: boolean; currency: string; compare: { avgProfit: number | null; best: { name: string; profit: number } | null }; ideas: DishIdea[] }
export const getIdeas = (): Promise<IdeasResponse> => request({ path: '/api/v2/ideas', method: 'GET' });
export const refreshIdeas = (payload: { force?: boolean; language?: string }): Promise<IdeasResponse> => request({ path: '/api/v2/ideas/refresh', method: 'POST' }, payload);
export const addIdeaToMenu = (id: string, payload: { price?: number; name?: string } = {}): Promise<{ menuItemId: string }> => request({ path: `/api/v2/ideas/${encodeURIComponent(id)}/add`, method: 'POST' }, payload);
export const dismissIdea = (id: string): Promise<{ idea: DishIdea }> => request({ path: `/api/v2/ideas/${encodeURIComponent(id)}/dismiss`, method: 'POST' }, {});

// «ИИ-ассистент» (v2): Stripe customer portal that returns to the v2 page.
export const openAiBillingPortal = (lang?: string, returnBaseUrl?: string): Promise<StripePortalResponse> =>
  request<StripePortalResponse>(ApiEndpoints.billing.stripePortal, { lang, returnPath: '/v2/ai', returnBaseUrl });

// Allergens and dish labels (v2).
export interface LabelsResponse {
  allergenCodes: string[]; tagCodes: string[];
  products: Record<string, string[]>;
  dishes: Record<string, { extra: string[]; tags: string[]; confirmed: boolean }>;
  fromRecipe: Record<string, string[]>;
}
export const getLabels = (): Promise<LabelsResponse> => request({ path: '/api/v2/labels', method: 'GET' });
export const setProductAllergens = (id: string, allergens: string[]): Promise<{ ingredientId: string; allergens: string[] }> =>
  request({ path: `/api/v2/ingredients/${encodeURIComponent(id)}/allergens`, method: 'PUT' }, { allergens });
export const setDishLabels = (id: string, payload: { extra: string[]; tags: string[]; confirmed: boolean }): Promise<{ menuItemId: string; extra: string[]; tags: string[]; confirmed: boolean }> =>
  request({ path: `/api/v2/dishes/${encodeURIComponent(id)}/labels`, method: 'PUT' }, payload);
export const getMenuLabels = (): Promise<{ labels: Record<string, { allergens: string[]; tags: string[] }> }> => request({ path: '/api/v2/menu/labels', method: 'GET' });
export interface VenueAnswers { wifiName?: string; wifiPassword?: string; toilet?: string; parking?: string; payment?: string; kids?: string; pets?: string; events?: string; faq?: { q: string; a: string }[] }
export const getVenueAnswers = (): Promise<{ answers: VenueAnswers }> => request({ path: '/api/v2/venue-answers', method: 'GET' });
export const saveVenueAnswers = (answers: VenueAnswers): Promise<{ answers: VenueAnswers }> => request({ path: '/api/v2/venue-answers', method: 'PUT' }, answers);

// ── Guest assistant (the paid «ИИ-ассистент»; 20 free demo questions)
export type GuestAssistantAction =
  | { type: 'add'; items: { id: string; qty: number }[] }
  | { type: 'callWaiter'; reason?: string }
  | { type: 'bill' };
export interface GuestAssistantReply { reply: string; dishes: string[]; action: GuestAssistantAction | null; demoLeft?: number | null; local?: boolean }
export interface GuestAssistantState { enabled: boolean; paid: boolean; free?: boolean; demoLeft: number; demoLimit: number; conversations: number; included: number; available: boolean }
export const getGuestAssistantStatus = (): Promise<{ available: boolean }> => request({ path: '/api/v2/guest-assistant/status', method: 'GET' });
export const getGuestAssistant = (): Promise<GuestAssistantState> => request({ path: '/api/v2/guest-assistant', method: 'GET' });
export const setGuestAssistant = (enabled: boolean): Promise<GuestAssistantState> => request({ path: '/api/v2/guest-assistant', method: 'PUT' }, { enabled });
// A guest carries the table token (from the table code), like an order does.
export const askGuestAssistant = async (payload: { tableId: string; messages: { role: 'user' | 'assistant'; content: string }[]; language: string; conversationId: string; orderNote?: string }): Promise<GuestAssistantReply> => {
  const headers: Record<string, string> = { ...(buildJsonHeaders(payload) || {}), 'X-Cafe-Table-Token': readTableOrderToken(payload.tableId) };
  const response = await fetch(resolveApiUrl('/api/v2/guest-assistant/chat'), { method: 'POST', headers, body: JSON.stringify(payload) });
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(String((body && body.error) || response.statusText), response.status, body);
  return body as GuestAssistantReply;
};
// The assistant's voice (mp3) and listening (recorded speech → text).
export const guestAssistantVoice = async (tableId: string, text: string, kind: 'greeting' | 'reply'): Promise<Blob> => {
  const payload = { tableId, text, kind };
  const headers: Record<string, string> = { ...(buildJsonHeaders(payload) || {}), 'X-Cafe-Table-Token': readTableOrderToken(tableId) };
  const response = await fetch(resolveApiUrl('/api/v2/guest-assistant/voice'), { method: 'POST', headers, body: JSON.stringify(payload) });
  if (!response.ok) throw new ApiError('Voice is not available', response.status, null);
  return response.blob();
};
export const guestAssistantListen = async (tableId: string, audio: Blob): Promise<{ text: string }> => {
  const headers: Record<string, string> = { ...(buildJsonHeaders() || {}), 'Content-Type': audio.type || 'audio/webm', 'X-Cafe-Table-Token': readTableOrderToken(tableId) };
  const response = await fetch(resolveApiUrl(`/api/v2/guest-assistant/listen?tableId=${encodeURIComponent(tableId)}`), { method: 'POST', headers, body: audio });
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(String((body && body.error) || response.statusText), response.status, body);
  return body as { text: string };
};
// What guests ordered from dishes the assistant put in the cart (for the owner's report).
export const reportGuestAssistantOrder = async (tableId: string, conversationId: string, items: { id: string; qty: number }[]): Promise<void> => {
  const payload = { tableId, conversationId, items };
  const headers: Record<string, string> = { ...(buildJsonHeaders(payload) || {}), 'X-Cafe-Table-Token': readTableOrderToken(tableId) };
  await fetch(resolveApiUrl('/api/v2/guest-assistant/ordered'), { method: 'POST', headers, body: JSON.stringify(payload) }).catch(() => undefined);
};
export interface GuestAssistantReport {
  period: 'day' | 'week' | 'month';
  currency: string;
  plan: { paid: boolean; enabled: boolean; demoLeft: number; conversationsMonth: number; included: number };
  conversations: number;
  questions: number;
  orders: number;
  revenue: number;
  dishesOrdered: number;
  fromAdvice: number;
  topics: Record<string, number>;
  topQuestions: { text: string; count: number }[];
  unknown: { text: string; count: number }[];
  allergensUnchecked: { id: string; name: string; count: number }[];
  topDishes: { id: string; name: string; count: number }[];
}
export const getGuestAssistantReport = (period: 'day' | 'week' | 'month'): Promise<GuestAssistantReport> => request({ path: `/api/v2/guest-assistant/report?period=${period}`, method: 'GET' });
// Owner (Helfer): AI billing on/off for every venue, through the owner key.
export interface OwnerAiVenue { id: string; name: string; email: string; profileId: string; free: boolean; paid: boolean; subStatus: string; periodEnd: string; lastSeenAt: string }
export interface OwnerAiVenues { hasKey: boolean; keyTail: string; ready: boolean; error: string; self: string; venues: OwnerAiVenue[] }
export const getOwnerAiVenues = (ownerEmail: string): Promise<OwnerAiVenues> =>
  request({ path: `/api/v2/owner/ai-venues?ownerEmail=${encodeURIComponent(ownerEmail)}`, method: 'GET' });
export const setOwnerAiVenueFree = (ownerEmail: string, id: string, free: boolean): Promise<{ id: string; free: boolean }> =>
  request({ path: `/api/v2/owner/ai-venues/${encodeURIComponent(id)}`, method: 'PUT' }, { ownerEmail, free });
export const createOwnerPortalKey = (ownerEmail: string): Promise<{ key: string }> =>
  request({ path: '/api/v2/owner/portal-key', method: 'POST' }, { ownerEmail });
